import { Component } from '@theme/component';
import { animateOut } from '@theme/r-motion';

/**
 * @theme/r-toast — the shared toast/notification primitive (WS15).
 *
 * The theme had no toast pattern (feedback was inline aria-live regions only).
 * One region, rendered once by snippets/r-toast.liquid; call showToast() for
 * transient global messages. Prefer inline feedback where a control owns the
 * state (add-to-cart confirmation, form errors next to their field) — toasts
 * are for messages with no natural anchor.
 */

/** Max simultaneously visible toasts — the oldest is dropped beyond this. */
const MAX_TOASTS = 2;

/** Default time a toast stays up. Long enough to read twice; tap dismisses. */
const DEFAULT_DURATION = 5000;

export class RToastRegion extends Component {
  /**
   * @param {object} options
   * @param {string} options.message
   * @param {'neutral' | 'error'} [options.tone]
   * @param {number} [options.duration] ms before auto-dismiss
   * @returns {HTMLElement}
   */
  show({ message, tone = 'neutral', duration = DEFAULT_DURATION }) {
    const toast = document.createElement('div');
    // Entrance rides the shared .r-motion-rise-in utility (r-base.css)
    toast.className = `r-toast r-toast--${tone} r-motion-rise-in`;
    toast.textContent = message;
    this.append(toast);

    while (this.children.length > MAX_TOASTS) {
      this.firstElementChild?.remove();
    }

    const timeout = setTimeout(async () => {
      await animateOut(toast, { preset: 'fade', duration: 'quick' });
      toast.remove();
    }, duration);

    toast.addEventListener('pointerdown', () => {
      clearTimeout(timeout);
      toast.remove();
    });

    return toast;
  }
}

if (!customElements.get('r-toast-region')) {
  customElements.define('r-toast-region', RToastRegion);
}

/**
 * Shows a toast in the global region (rendered by snippets/r-toast.liquid).
 * Safe to call before the region upgrades — returns null and stays silent.
 *
 * @param {string} message
 * @param {{ tone?: 'neutral' | 'error', duration?: number }} [options]
 * @returns {HTMLElement | null}
 */
export function showToast(message, options = {}) {
  const region = document.querySelector('r-toast-region');
  if (!(region instanceof RToastRegion)) return null;
  return region.show({ message, ...options });
}
